import { fertilityProductFormats } from 'constants/products';
import { applicationTypes, scriptMethods, scriptTypes } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';
import { inputStates } from 'constants/values';
import { get, has, isNull, mapValues, pick, sortBy, isNil, first } from 'lodash';

// selectors
import { getCropById, getCropIdByCrop, getNominalWeightByCropId, getBushelAcreCropForCrop } from 'crops/selectors';
import {
  getFormatForProduct,
  getNitrogenRateByFertilizerRateAndProduct,
  getNitrapyrinApplication
} from 'products/fertility-selectors';
import {
  getApplicationTypeForScriptType,
  getProductYearFromSeasonCode,
  getScriptTypeForScript,
  isAdvancedScript
} from 'scripts/selectors';
import {
  getFertilityScriptTypeBySubstanceTypeUuid
} from 'scripts/fertility-selectors';

// utils
import { convert } from 'util/unitConversion';
import { formatNumberRoundToNearest } from 'util/formatters/numberFormatter';
import { calcAreaForZoneGeoJson } from 'util/zoneOperations';


import { getIncorporationMethodForScript } from 'scripts/application-selectors';
import { getFormatFromApplicationIncorporationMethod } from 'products/selectors';
export const parseEstimatedGrossRevenue = ({ estimatedGrossRevenue }) => {
  return convert({ from: estimatedGrossRevenue, to: { u: UNIT_TYPES.usd_acre } });
};

export const parseEstimatedYield = ({ bushelAcreCrop, cropNominalWeight, estimatedYield }) => {
  const bushelAcreUnitKey = `${bushelAcreCrop}_bushel_acre`;
  return convert({
    from: estimatedYield,
    to: { u: UNIT_TYPES[bushelAcreUnitKey] },
    cropNominalWeight
  });
};

export const parseLiquid = ({ liquid }) => {
  if (isNull(liquid) || !has(liquid, 'q')) {
    return { q: liquid, u: UNIT_TYPES.gal_ac };
  } if (isNull(get(liquid, 'q'))) {
    return { q: liquid.q, u: UNIT_TYPES.gal_ac };
  } else {
    return convert({ from: liquid, to: { u: UNIT_TYPES.gal_ac }});
  }
};

export const parseProduct = ({ product, scriptProducts }) => {
  // if a product is assigned to the zone, replace with the
  // expanded product from scriptProducts

  if (product) {
    // planting scripts zones have legacy ids as 'id', and sprout uuids as 'uuid', 
    // we are interested in the sprout one.
    const { uuid, id } = product;
    return scriptProducts.find((expandedProduct) =>
      expandedProduct.id === (uuid || id) );
  } else {
    // make the first product the default, otherwise make it null
    if (scriptProducts.length) {
      return scriptProducts[0];
    }
    return null;
  }
};

// passing script type here because we can make planting scripts off
// fertility zones & vice versa, so when we parse rate, we need to know
// what kind of units we want
export const parseRate = ({ formatFromIncorporationMethod, isNewAdvancedScript, rate, scriptProducts, scriptType }) => {
  let unit;
  if (scriptType === scriptTypes.PLANTING) {
    unit = UNIT_TYPES.seed_acre;
  } else {
    const [product] = scriptProducts;
    const format = scriptType === applicationTypes.APPLICATION
      ? formatFromIncorporationMethod
      : getFormatForProduct(product);
    unit = format === fertilityProductFormats.WET ? UNIT_TYPES.gal_ac : UNIT_TYPES.pound_acre;
  }

  // if there's no q (bare rate), then plug in q with appropriate unit
  if(!has(rate, 'q')) {
    return { q: rate, u: unit };
  }

  // if there's a q, send through conversion
  let converted = convert({ from: rate, to: { u: unit } });
  if (isNewAdvancedScript) {
    converted.q = formatNumberRoundToNearest(converted.q, 100);
  }
  return converted;
};

export const parseTargetYield = ({ cropNominalWeight, targetYield }) => {
  const toUnitKey = UNIT_TYPES.bu_ac;
  return has(targetYield, 'q')
    ? convert({
      from: targetYield,
      to: { u: toUnitKey },
      cropNominalWeight
    })
    : { q: targetYield || 0, u: toUnitKey };
  // Adding above short circuit conditional to make code more robust
  // Prompted by unexpected/breaking null value for targe-yield
};

export const parseCropIdFromScript = (state, script) => {
  const crop = get(script, 'crop') || get(script, 'attributes.crop');

  return get(script, 'attributes.crop-id') || getCropIdByCrop(state, crop.toString().toLowerCase()) || null;
};

export const initBaseScript = (script) => {
  return pick(script, [
    'activity-type',
    'activity-uuid',
    'attributes',
    'bases',
    'cropping-year',
    'create-time',
    'field-id',
    'field-uuid',
    'fmz-id',
    'fmz-name',
    'fmz-version',
    'id',
    'name',
    'primary',
    'program-instance-id',
    'season',
    'summary',
    'tag',
    'update-time',
    'warnings'
  ]);
};

export const buildScriptMeta = ({
  fieldId,
  getState,
  isInSync=true,
  script,
  scriptProducts,
  templateParams
}) => {
  const state = getState();
  const { name } = script;
  const { cropId: templateParamsCropId, scriptType } = templateParams;

  let scriptMeta = {
    fieldId,
    name,
    products: scriptProducts,
    scriptState: isInSync ? inputStates.INSYNC : inputStates.CHANGED
  };

  const scriptTypeToCheck = scriptType || getScriptTypeForScript(script);
  const isPlanting = scriptTypeToCheck === scriptTypes.PLANTING;
  if ( isPlanting ) {
    const cropId = templateParamsCropId || parseCropIdFromScript(state, script);
    let crop = getCropById(state, cropId);
    if (cropId && crop) {
      crop = crop.toLowerCase();
      scriptMeta = {...scriptMeta, crop, cropId };
    }
  }

  return scriptMeta;
};

export const buildSeedAndFertilizerApplicationsForPlantingScriptZoneProperties = ({
  isAdvancedScript,
  plantingPlanParams,
  properties
}) => {
  let seedApplication = pick(properties,['estimated-profit','estimated-yield','product','rate','target-yield']);
  let { liquid } = properties;

  if (plantingPlanParams) {
    const { hasPlantingFertilizerApplication } = plantingPlanParams;

    // if there's no fertilizer application currently, but planting plan params are indicating one will
    // be used, then plug in 0 as an initial value so that liquid column will be shown
    if (hasPlantingFertilizerApplication) {
      liquid = 0;
    }

    // planting plan rate -- blank these out only if not advanced script
    if (!isAdvancedScript) {
      seedApplication.rate = { q: 0, u: UNIT_TYPES.seed_acre };
      seedApplication['target-yield'] = { q: 0, u: UNIT_TYPES.bu_ac };
    }
  }

  return {
    [applicationTypes.FERTILIZER]: [{ rate: parseLiquid({ liquid }) }],
    [applicationTypes.SEED]: [seedApplication]
  };
};

export const buildFertilizerAndStabilizerApplicationsForFertilityScriptZoneProperties = ({
  getState,
  plantingPlanParams,
  properties,
  scriptType
}) => {
  const state = getState();
  let fertilizerApplication = state !== null && !isNil(get(properties, 'chemical-mix')) 
    ? pick(properties, ['chemical-mix', 'rate'])
    : pick(properties, ['product','rate']);

  fertilizerApplication.incorporationMethod = properties['incorporation-method'];
  const { product, rate } = fertilizerApplication;
  const firstProduct = first(fertilizerApplication['chemical-mix']);
  if (rate) {
    fertilizerApplication = {
      ...fertilizerApplication,
      'nitrogen-rate': getNitrogenRateByFertilizerRateAndProduct(state, rate, product ? product : firstProduct)
    };
  }

  let stabilizerApplication = properties.stabilizer;

  if (plantingPlanParams) {
    const {
      hasNitrapyrin,
      incorporationMethod: plantingPlanIncorporationMethod
    } = plantingPlanParams;

    if (hasNitrapyrin) {
      stabilizerApplication = getNitrapyrinApplication(state);
    }

    fertilizerApplication = {
      ...fertilizerApplication,
      incorporationMethod: plantingPlanIncorporationMethod
    };
  }

  const applicationType = getApplicationTypeForScriptType(scriptType);

  return {
    [applicationType]: [fertilizerApplication],
    [applicationTypes.STABILIZER]: [stabilizerApplication]
  };
};


export const buildProductApplicationsForZoneProperties = ({
  getState,
  isAdvancedScript,
  plantingPlanParams,
  properties,
  scriptType
}) => {
  switch (scriptType) {
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return buildFertilizerAndStabilizerApplicationsForFertilityScriptZoneProperties({
        getState,
        plantingPlanParams,
        properties,
        scriptType
      });
    case scriptTypes.PLANTING:
      return buildSeedAndFertilizerApplicationsForPlantingScriptZoneProperties({
        isAdvancedScript,
        plantingPlanParams,
        properties
      });
    default:
      return null;
  }
};

export const buildZoneGeoJson = ({ geometry }) => {
  return {
    geometry,
    properties: {},
    type: 'Feature'
  };
};

export const buildZoneMeta = ({
  geometry,
  getState,
  index,
  isAdvancedScript,
  plantingPlanParams,
  properties,
  scriptType,
  zoneGeoJson
}) => {
  const { 'zone-id': zoneId, 'is-check-strip': isCheckStrip } = properties;
  const applications = buildProductApplicationsForZoneProperties({
    getState,
    isAdvancedScript,
    plantingPlanParams,
    properties,
    scriptType
  });

  // Zone area received from service is intentionally omitted, as area calculation will be replaced post-return
  // by a local calculation. See docs/drawing.md for further explanation.
  return {
    applications,
    area: calcAreaForZoneGeoJson(zoneGeoJson.geometry),
    geometry,
    index,
    inputState: inputStates.INSYNC,
    zoneId,
    isCheckStrip
  };
};

export const parseZoneProperty = ({ // NOSONAR Switch Statement exception
  cropId,
  formatFromIncorporationMethod,
  getState,
  isNewAdvancedScript,
  key,
  scriptProducts,
  scriptType,
  scrubProperties,
  value
}) => {

  const state = getState();
  const cropNominalWeight = getNominalWeightByCropId(state, cropId);
  const crop = getCropById(state, cropId);
  const bushelAcreCrop = getBushelAcreCropForCrop(crop);

  switch (key) {
    // TODO: for the sake of hotfixing, i'll leave estimatedGrossRevenue as the name, but we should
    // reconcile with the estimated profit nomenclature
    case 'estimated-profit':
      return parseEstimatedGrossRevenue({ estimatedGrossRevenue: value });
    case 'estimated-yield':
      return parseEstimatedYield({ bushelAcreCrop, cropNominalWeight, estimatedYield: value });
    case 'liquid':
      return parseLiquid({ liquid: scrubProperties ? null : value });
    case 'product':
      return parseProduct({
        getState,
        product: scrubProperties ? null : value,
        scriptProducts,
        scriptType
      });
    case 'rate':
      return parseRate({
        isNewAdvancedScript,
        formatFromIncorporationMethod,
        rate: scrubProperties ? null : value,
        scriptProducts,
        scriptType
      });
    case 'target-yield':
      return parseTargetYield({ bushelAcreCrop, cropNominalWeight, targetYield: scrubProperties ? null : value });
    default:
      return value;
  }
};

export const parseZoneProperties = ({
  cropId,
  formatFromIncorporationMethod,
  getState,
  isNewAdvancedScript,
  properties,
  scriptProducts,
  scrubProperties,
  scriptType
}) => {
  return mapValues(properties,(value,key) => {
    return parseZoneProperty({
      cropId,
      formatFromIncorporationMethod,
      getState,
      isNewAdvancedScript,
      key,
      scriptProducts,
      scriptType,
      scrubProperties,
      value
    });
  });
};

export const parseAdvancedScriptZoneProperties = ({ goalRatio, properties }) => {
  // structure of model
  // { 0-100:{}, 25-75:{}, 50-50:{}, 75-25:{}, 100-0:{} } = recommendationModel
  const goalRatioKey = `${goalRatio}-${100-goalRatio}`;
  const recommendationModel = get(properties, 'pop-recommendation.model-yield-economic-mixed');
  const { product } = recommendationModel;

  const goalModel = get(recommendationModel, goalRatioKey);
  const advancedScriptProperties = pick(goalModel, ['estimated-profit','estimated-yield', 'rate']);
  return {...properties, ...advancedScriptProperties, product};
};

export const parseScript = ({
  fieldId,
  getState,
  script,
  scriptProducts,
  templateParams={},
  scrubProperties=false
}) => {
  const { goalRatio, plantingPlanParams, seasonCode } = templateParams;
  const isNewAdvancedScript = isAdvancedScript(script) && Number.isFinite(goalRatio);
  const { geometry } = script;

  if(plantingPlanParams) {
    script.name = plantingPlanParams.scriptName || plantingPlanParams.defaultScriptName || script.name;
    const substanceTypeUuid = get(plantingPlanParams, 'substanceTypeUuid');
    script.tag = substanceTypeUuid
      ? getFertilityScriptTypeBySubstanceTypeUuid(getState(), substanceTypeUuid)
      : scriptMethods.MANUAL;
  }

  const scriptType = templateParams.scriptType || getScriptTypeForScript(script);
  const formatFromIncorporationMethod = getFormatFromApplicationIncorporationMethod(
    getIncorporationMethodForScript(script));
  const zones = {};
  // if passing a season and we're supposed to scrub properties, use the passed season for the script
  // instead of the one originally in the script
  if(seasonCode && scrubProperties) {
    script.season = seasonCode;
    script['cropping-year'] = getProductYearFromSeasonCode(seasonCode);
  }

  let scriptMeta = buildScriptMeta({ fieldId, getState, script, scriptProducts, templateParams });
  const { cropId } = scriptMeta;

  (geometry.features.slice()).forEach((zone, index) => {
    let { geometry, properties } = zone;
    const { 'zone-id': zoneId } = properties;

    // if new advanced script retrieve the correct model from properties
    if (isNewAdvancedScript) {
      properties = parseAdvancedScriptZoneProperties({ goalRatio, properties });
      scriptProducts = [ get(properties, 'product') ];
    }

    properties = parseZoneProperties({
      cropId,
      formatFromIncorporationMethod,
      getState,
      isNewAdvancedScript,
      properties,
      scriptProducts,
      scrubProperties,
      scriptType
    });
    
    // geoJson here is meant to hold just geometry and not field or RX data. The .properties member is
    // intentionally empty.
    const zoneGeoJson = buildZoneGeoJson(zone);

    const zoneMeta = buildZoneMeta({
      geometry,
      getState,
      index,
      isAdvancedScript: isAdvancedScript(script),
      plantingPlanParams,
      properties,
      scriptType,
      zoneGeoJson
    });

    zones[zoneId] = { meta: zoneMeta, geoJson: zoneGeoJson };
  });

  // if new advanced script, redo script meta with appropriate products
  if (isNewAdvancedScript) {
    scriptMeta = buildScriptMeta({ fieldId, getState, script, scriptProducts, templateParams });
  }

  const baseScript = initBaseScript(script);
  return {
    ...baseScript,
    meta: scriptMeta,
    zones
  };
};

export const parseScriptListWithFieldId = (scriptList, fieldId) => {
  const scripts = {};

  const isValidScript = (script) => (
    // Checks the raw script for missing data. Script is skipped if FALSE is returned.
    script.hasOwnProperty('season')
    && script.hasOwnProperty('activity-type')
  );

  scriptList.forEach( (script) => {
    if( isValidScript(script) ) {
      scripts[script.id] = {...script, meta: { fieldId: fieldId || script['field-id'].toString() }};
    }
  });
  return scripts;
};

export const parseScriptsForFertilityActivityUuid = (rawScriptList) => {
  const { result } = rawScriptList;

  return sortBy(result, ['update-time']).map(({ 'rx-uuid': uuid }) => uuid);
};

export const parseManyScripts = ({rawScripts}) => {
  // Yeah, it's simple, but it maintains the abstraction
  const { result } = rawScripts;
  return result || [];
};
